import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAuth } from "firebase/auth";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, X } from "lucide-react";
import "./AnswerKeyCorrectionDialog.css";
import { API_BASE_URL } from "../utils/apiBase";

const FOCUSABLE = "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const optionText = (option) => (option ? `${option.label}. ${option.text}` : "No valid key");
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const score = (outcome) => {
  if (!outcome || outcome.status !== "complete") return "Not complete";
  return `${outcome.totalAwarded}/${outcome.totalPossible} (${outcome.percentage}%)${outcome.grade ? ` · ${outcome.grade}` : ""}`;
};

async function request(path, { method = "GET", body } = {}) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("You must be signed in to correct answer keys.");
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    cache: "no-store",
    headers: { Authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(data.error || "The request failed."), { code: data.code, preview: data.preview });
  return data;
}

/**
 * Two-step answer-key correction for already-marked MCQ responses:
 * preview the exact effect, then explicitly apply that reviewed plan.
 */
const AnswerKeyCorrectionDialog = ({ quizId, studentNames = {}, onClose, onApplied }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionId, setQuestionId] = useState("");
  const [correctOptionId, setCorrectOptionId] = useState("");
  const [reason, setReason] = useState("");
  const [includeUncertain, setIncludeUncertain] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  const question = useMemo(() => questions.find((item) => item.questionId === questionId) || null, [questions, questionId]);
  const encodedQuiz = encodeURIComponent(quizId);

  useEffect(() => {
    let cancelled = false;
    request(`/api/quizzes/${encodedQuiz}/answer-key-corrections`)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data.questions) ? data.questions : [];
        setQuestions(list);
        if (list.length === 1) {
          setQuestionId(list[0].questionId);
          setCorrectOptionId(list[0].correctOptionId);
        }
      })
      .catch((loadError) => { if (!cancelled) setError(loadError.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [encodedQuiz]);

  const close = useCallback(() => { if (busy !== "apply") onClose(); }, [busy, onClose]);

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [close]);

  const resetPreview = () => { setPreview(null); setConfirmed(false); setNotice(""); setResult(null); };

  const chooseQuestion = (id) => {
    const next = questions.find((item) => item.questionId === id);
    setQuestionId(id);
    setCorrectOptionId(next?.correctOptionId || "");
    setIncludeUncertain(false);
    setError("");
    resetPreview();
  };

  const body = { questionId, correctOptionId, reason, includeUncertain };
  const canPreview = question && correctOptionId && correctOptionId !== question.correctOptionId && reason.trim();

  const runPreview = async (overrides = {}) => {
    setBusy("preview");
    setError("");
    resetPreview();
    try {
      const data = await request(`/api/quizzes/${encodedQuiz}/answer-key-corrections/preview`, { method: "POST", body: { ...body, ...overrides } });
      setPreview(data.preview);
    } catch (previewError) {
      setError(previewError.message);
    } finally {
      setBusy("");
    }
  };

  const runApply = async () => {
    setBusy("apply");
    setError("");
    try {
      const data = await request(`/api/quizzes/${encodedQuiz}/answer-key-corrections/apply`, {
        method: "POST", body: { ...body, previewFingerprint: preview.previewFingerprint },
      });
      setResult(data);
      if (data.status === "completed") onApplied?.(data);
    } catch (applyError) {
      if (applyError.code === "stale-preview" && applyError.preview) {
        setPreview(applyError.preview);
        setConfirmed(false);
        setNotice("Responses changed since your preview. Here is the updated preview: please review it again before applying.");
      } else {
        setError(applyError.message);
      }
    } finally {
      setBusy("");
    }
  };

  const counts = preview?.counts;
  const changedResponses = preview ? preview.responses.filter(({ state }) => state === "will-change") : [];

  return createPortal(
    <div className="akcd-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div ref={dialogRef} className="akcd-dialog" role="dialog" aria-modal="true" aria-labelledby="akcd-title" aria-describedby="akcd-intro">
        <button ref={closeRef} type="button" className="akcd-close" onClick={close} aria-label="Close" disabled={busy === "apply"}>
          <X size={18} aria-hidden="true" />
        </button>
        <h2 id="akcd-title">Correct a multiple-choice answer key</h2>
        <p id="akcd-intro" className="akcd-muted">
          Preview exactly which marked responses change, then apply. Student answers are kept, teacher-set marks are
          left alone, and no AI marking or credits are used.
        </p>

        {loading && <p className="akcd-state" role="status"><Loader2 className="akcd-spin" size={16} aria-hidden="true" /> Loading questions…</p>}
        {!loading && !questions.length && !error && <p className="akcd-state">This test has no multiple-choice questions.</p>}

        {questions.length > 0 && !result && (
          <div className="akcd-form">
            <label className="akcd-label" htmlFor="akcd-question">Question</label>
            <select id="akcd-question" value={questionId} onChange={(event) => chooseQuestion(event.target.value)} disabled={Boolean(busy)}>
              <option value="">Choose a question…</option>
              {questions.map((item, index) => (
                <option key={item.questionId} value={item.questionId}>
                  {`Q${index + 1}. ${item.questionText.slice(0, 90)}${item.questionText.length > 90 ? "…" : ""}`}
                </option>
              ))}
            </select>

            {question && (
              <>
                <p className="akcd-question-text">{question.questionText}</p>
                <fieldset className="akcd-options" disabled={Boolean(busy)}>
                  <legend className="akcd-label">Correct answer</legend>
                  {question.options.map((option) => (
                    <label key={option.id} className={`akcd-option${option.id === correctOptionId ? " akcd-option-selected" : ""}`}>
                      <input
                        type="radio"
                        name="akcd-correct-option"
                        value={option.id}
                        checked={option.id === correctOptionId}
                        onChange={() => { setCorrectOptionId(option.id); resetPreview(); }}
                      />
                      <span><strong>{option.label}.</strong> {option.text}</span>
                      {option.id === question.correctOptionId && <em className="akcd-tag">Current key</em>}
                    </label>
                  ))}
                </fieldset>
                <label className="akcd-label" htmlFor="akcd-reason">Reason for the correction</label>
                <textarea
                  id="akcd-reason"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  disabled={Boolean(busy)}
                  onChange={(event) => { setReason(event.target.value); resetPreview(); }}
                  placeholder="For example: NOT 1 = 0, then 0 OR 0 = 0, so the answer is 0."
                />
                {question.corrections?.length > 0 && (
                  <p className="akcd-muted">
                    Earlier corrections: {question.corrections.map((item) =>
                      `${item.from?.label || "?"} → ${item.to?.label || "?"} (${item.status})`).join("; ")}
                  </p>
                )}
                <div className="akcd-actions">
                  <button type="button" className="akcd-secondary" onClick={close}>Cancel</button>
                  <button type="button" className="akcd-primary" onClick={() => runPreview()} disabled={!canPreview || Boolean(busy)}>
                    {busy === "preview" ? <Loader2 className="akcd-spin" size={16} aria-hidden="true" /> : null}
                    Preview changes
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {error && <p className="akcd-error" role="alert"><AlertTriangle size={16} aria-hidden="true" /> {error}</p>}
        {notice && <p className="akcd-notice" role="status">{notice}</p>}

        {preview && !result && (
          <section className="akcd-preview" aria-labelledby="akcd-preview-title">
            <h3 id="akcd-preview-title">
              Preview: {optionText(preview.currentKey)} <ArrowRight size={15} aria-label="changes to" /> {optionText(preview.correctedKey)}
            </h3>
            {preview.scopeQuizzes.length > 1 && (
              <p className="akcd-warning">
                This question is used in {plural(preview.scopeQuizzes.length, "test")}, so all of them are corrected:{" "}
                {preview.scopeQuizzes.map((quiz) => quiz.title || quiz.quizCode || quiz.quizId).join(", ")}.
              </p>
            )}
            <ul className="akcd-counts">
              <li><strong>{plural(counts.willChange, "marked response")}</strong> will change
                {counts.willChange > 0 && ` (${counts.gainMark} gain a mark, ${counts.loseMark} lose a mark)`}.</li>
              {counts.unchanged > 0 && <li>{plural(counts.unchanged, "response")} keep the same mark.</li>}
              {counts.teacherOverrides > 0 && <li>{plural(counts.teacherOverrides, "teacher-set mark")} will not be changed.</li>}
              {counts.unmarked > 0 && <li>{plural(counts.unmarked, "response")} not marked yet will be marked with the corrected key.</li>}
              {counts.unresolvedAnswers > 0 && (
                <li>{plural(counts.unresolvedAnswers, "response")} can't be matched to an option the student saw, so {counts.unresolvedAnswers === 1 ? "it is" : "they are"} left for you to check.</li>
              )}
              {counts.activeAttempts > 0 && (
                <li>{plural(counts.activeAttempts, "attempt")} still in progress will be marked with the corrected key when submitted.</li>
              )}
              {counts.uncertainOrigin > 0 && !counts.uncertainIncluded && (
                <li>{plural(counts.uncertainOrigin, "older mark")} can't be confirmed as automatic (it may have been entered by hand), so {counts.uncertainOrigin === 1 ? "it is" : "they are"} kept as {counts.uncertainOrigin === 1 ? "it is" : "they are"}.</li>
              )}
              {counts.uncertainOrigin > 0 && counts.uncertainIncluded && (
                <li>{plural(counts.uncertainOrigin, "older mark")} that can't be confirmed as automatic {counts.uncertainOrigin === 1 ? "is" : "are"} included in the changes above.</li>
              )}
              {counts.unavailableProjections > 0 && (
                <li>{plural(counts.unavailableProjections, "attempt")} can't be projected exactly; {counts.unavailableProjections === 1 ? "its" : "their"} stored total is recalculated when you apply.</li>
              )}
            </ul>
            {counts.uncertainOrigin > 0 && (
              <label className="akcd-confirm akcd-include">
                <input
                  type="checkbox"
                  checked={includeUncertain}
                  disabled={Boolean(busy)}
                  onChange={(event) => { setIncludeUncertain(event.target.checked); runPreview({ includeUncertain: event.target.checked }); }}
                />
                Also re-mark the older marks that can't be confirmed as automatic.
              </label>
            )}
            {preview.limits && !preview.limits.withinLimit && (
              <p className="akcd-error" role="alert">
                <AlertTriangle size={16} aria-hidden="true" /> This correction affects too many responses to apply in one go. Nothing will be changed. Please contact support.
              </p>
            )}

            {preview.attempts.length > 0 ? (
              <div className="akcd-table-wrap">
                <table className="akcd-table">
                  <caption className="akcd-sr-only">Score changes by attempt</caption>
                  <thead>
                    <tr><th scope="col">Student</th><th scope="col">Attempt</th><th scope="col">Score</th><th scope="col">Final result</th></tr>
                  </thead>
                  <tbody>
                    {preview.attempts.map((attempt) => (
                      <tr key={`${attempt.quizId}-${attempt.attemptId}-${attempt.studentUid}`}>
                        <th scope="row">{studentNames[attempt.studentUid] || "Student"}</th>
                        <td>{attempt.attemptNumber ?? "—"}</td>
                        <td>{attempt.projection === "ok"
                          ? <>{score(attempt.before)} <ArrowRight size={13} aria-label="becomes" /> {score(attempt.after)}</>
                          : <span className="akcd-unavailable">Projection unavailable{attempt.reason ? `: ${attempt.reason}` : ""}</span>}</td>
                        <td>{attempt.projection === "ok" && attempt.finalProjection === "ok"
                          ? <>{score(attempt.finalBefore)} <ArrowRight size={13} aria-label="becomes" /> {score(attempt.finalAfter)}</>
                          : <span className="akcd-unavailable">Projection unavailable</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="akcd-muted">No stored scores change.</p>
            )}
            {changedResponses.length > 0 && (
              <details className="akcd-details">
                <summary>Responses that change ({changedResponses.length})</summary>
                <ul>
                  {changedResponses.map((entry) => (
                    <li key={entry.submissionId}>
                      {studentNames[entry.studentUid] || "Student"}: chose {entry.selected ? optionText(entry.selected) : "no answer"},
                      mark {entry.currentMark} → {entry.newMark}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <label className="akcd-confirm">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={Boolean(busy)} />
              I've reviewed these changes and want to apply them.
            </label>
            <div className="akcd-actions">
              <button type="button" className="akcd-secondary" onClick={resetPreview} disabled={Boolean(busy)}>Back</button>
              <button type="button" className="akcd-primary akcd-apply" onClick={runApply}
                disabled={!confirmed || Boolean(busy) || preview.limits?.withinLimit === false}>
                {busy === "apply" ? <Loader2 className="akcd-spin" size={16} aria-hidden="true" /> : null}
                Apply correction
              </button>
            </div>
          </section>
        )}

        {result && (
          <section className="akcd-result" role="status">
            {result.status === "completed" ? (
              <p><CheckCircle2 size={18} aria-hidden="true" /> {result.alreadyApplied
                ? "This correction had already been applied. Nothing else changed."
                : `Correction applied: ${plural(result.changed, "response")} re-marked and ${plural(result.attemptsRecalculated, "attempt")} recalculated.`}
                {result.skippedChanged > 0 && ` ${plural(result.skippedChanged, "response")} changed while applying and ${result.skippedChanged === 1 ? "was" : "were"} left as they were.`}
              </p>
            ) : (
              <>
                <p className="akcd-warning">
                  The key and marks were corrected, but {plural(result.recalculationFailures?.length || 0, "attempt total")} could not be
                  recalculated yet. Retrying is safe and won't change any mark twice.
                </p>
                <div className="akcd-actions">
                  <button type="button" className="akcd-primary" onClick={runApply} disabled={Boolean(busy)}>Retry</button>
                </div>
              </>
            )}
            <div className="akcd-actions">
              <button type="button" className="akcd-secondary" onClick={close}>Close</button>
            </div>
          </section>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AnswerKeyCorrectionDialog;
